// Layer 4 (UI): the plant model's flow strip (polish.P11, comp variant A,
// picked 25 Sep). DESIGN.md § Strip and stepper: one card, cells split by
// hairlines with a rotated chevron notch at each boundary; the selected cell
// takes the accent-selected wash and a 3px inset crimson bottom rule and
// nothing else changes. The strip is the page's tab bar — its selected cell
// drives the stage panel and the tab set below. The cell recipe is the one
// `PipelineStrip`, `DowntimeImpact` and `QualityUi` draw; each route keeps its
// own cell because each strip's contents differ.
import { cn } from '@/shared/cn';
import type { FlowCell, FlowCircuitCell, FlowContextCell, FlowSampleFlag, FlowTone } from './PlantFlowStrip.transform';

// The chevron notch at each boundary belongs to the cell on its left, but is
// drawn by the cell on its right (`before:` at left -6px), so it never widens
// a cell's own scroll box — the no-clipping probe measures every cell. Its
// face follows the left cell: selected, hovered or plain.
const NOTCH =
  "before:pointer-events-none before:absolute before:left-[-6px] before:top-1/2 before:z-10 before:hidden before:h-[10px] before:w-[10px] before:-translate-y-1/2 before:rotate-45 before:border-r before:border-t before:border-[var(--hairline-strong)] before:content-[''] lg:before:block before:bg-[var(--surface-2)] [.flow-cell:hover+&]:before:bg-[var(--surface-1)]";

const NOTCH_AFTER_SELECTED = 'before:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] [.flow-cell:hover+&]:before:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]';

const SELECTED_FACE =
  'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] shadow-[inset_0_-3px_0_var(--color-accent-500)]';

const IDLE_FACE = 'bg-[var(--surface-2)] hover:bg-[var(--surface-1)]';

/** Water is the utility loop, not a step in the ore's path: a dashed boundary and a hatched face. */
const UTILITY_FACE =
  'border-l border-dashed border-l-[var(--hairline-strong)] bg-[repeating-linear-gradient(135deg,transparent_0_6px,var(--hairline)_6px_7px)]';

const DOT_COLOR: Record<FlowTone, string> = {
  ok: 'bg-[var(--state-online)]',
  warn: 'bg-[var(--state-warning)]',
  bad: 'bg-[var(--state-blocked)]',
};

const TONE_WASH: Record<'warn' | 'bad', string> = {
  warn: 'bg-[var(--wash-warning)] text-[var(--state-warning)]',
  bad: 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]',
};

const TONE_BAR: Record<'warn' | 'bad', string> = {
  warn: 'bg-[var(--state-warning)]',
  bad: 'bg-[var(--state-blocked)]',
};

function StatePill({ tone, children }: { tone: 'warn' | 'bad'; children: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]',
        TONE_WASH[tone],
      )}
    >
      {children}
    </span>
  );
}

function SampleFlag({ flag }: { flag: FlowSampleFlag }) {
  return (
    <span
      data-flow-sample={flag.id}
      title={flag.title}
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md border px-1.5 font-mono text-[10px] leading-4',
        flag.tone === 'bad'
          ? 'border-[color-mix(in_srgb,var(--state-blocked)_40%,transparent)] bg-[color-mix(in_srgb,var(--state-blocked)_9%,transparent)] text-[var(--state-blocked)]'
          : 'border-[color-mix(in_srgb,var(--state-warning)_40%,transparent)] text-[var(--state-warning)]',
      )}
    >
      {flag.id} {flag.word}
    </span>
  );
}

function CircuitCellBody({ cell }: { cell: FlowCircuitCell }) {
  return (
    <>
      <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
        <span
          aria-hidden
          title="SPC read of the circuit outcome at the cursor"
          className={cn('size-[7px] shrink-0 rounded-full', DOT_COLOR[cell.status])}
        />
        <span className="min-w-0 text-xs font-semibold uppercase tracking-[0.07em] text-ink-primary">
          {cell.name}
        </span>
        {cell.tag ? (
          <span className="ml-auto shrink-0" data-flow-tag>
            <StatePill tone={cell.tag.tone}>{cell.tag.label}</StatePill>
          </span>
        ) : null}
      </span>
      <span
        data-flow-figure={cell.id}
        className="flex flex-wrap items-baseline gap-x-1 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary"
      >
        {cell.figure}
        <span className="text-xs font-semibold tracking-normal text-muted">{cell.figureContext}</span>
      </span>
      {cell.comparator ? (
        <span data-flow-comparator={cell.id} className="text-xs leading-snug text-muted">
          vs <span className="font-semibold text-ink-secondary">{cell.comparator}</span>
        </span>
      ) : null}
      {cell.util ? (
        <span data-flow-util={cell.id} className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs tabular-nums text-ink-secondary">
          <span className="block h-1.5 w-[72px] shrink-0 overflow-hidden rounded-full bg-[var(--hairline)]">
            <span className={cn('block h-full rounded-full', TONE_BAR[cell.util.tone])} style={{ width: `${cell.util.pct}%` }} />
          </span>
          {cell.util.label}
        </span>
      ) : null}
      {cell.stagesLine ? (
        <span className="break-words text-[11px] leading-snug text-muted">{cell.stagesLine}</span>
      ) : null}
      <span className="mt-auto flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 pt-1.5">
        {cell.drift ? (
          <>
            <StatePill tone={cell.drift.tone}>{cell.drift.word}</StatePill>
            <span className="break-words text-[11px] leading-snug text-ink-secondary">{cell.drift.label}</span>
          </>
        ) : cell.quietLine ? (
          <span className="text-[11px] leading-snug text-muted">{cell.quietLine}</span>
        ) : null}
        {cell.samples.map((flag) => (
          <SampleFlag key={flag.id} flag={flag} />
        ))}
      </span>
    </>
  );
}

function ContextCellBody({ cell }: { cell: FlowContextCell }) {
  return (
    <>
      <span className="break-words text-xs font-semibold uppercase leading-tight tracking-[0.07em] text-muted [hyphens:auto]">
        {cell.name}
      </span>
      <span data-flow-figure={cell.id} className="break-words text-[15px] font-semibold leading-tight tabular-nums text-ink-secondary">
        {cell.headline}
      </span>
      {cell.sub ? <span className="break-words text-[11px] leading-snug text-muted">{cell.sub}</span> : null}
      {cell.samples.length ? (
        <span className="mt-auto flex min-w-0 flex-wrap gap-1 pt-1.5">
          {cell.samples.map((flag) => (
            <SampleFlag key={flag.id} flag={flag} />
          ))}
        </span>
      ) : null}
    </>
  );
}

export function PlantFlowStrip({
  cells,
  selectedId,
  onSelect,
}: {
  cells: readonly FlowCell[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  // A context cell never narrows past its widest word ("THICKENING" at the
  // label size, plus padding): below that it takes 106px and the circuits
  // share the rest, so no name breaks mid-word at 1280 or with the AI rail open.
  const template = cells
    .map((cell) => (cell.kind === 'circuit' ? 'minmax(0,1.55fr)' : 'minmax(106px,0.78fr)'))
    .join(' ');
  return (
    <nav
      aria-label="Plant flow"
      className="card-matte-2 relative overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div
        role="tablist"
        aria-label="Plant stages"
        className="grid grid-cols-2 sm:grid-cols-4 lg:[grid-template-columns:var(--flow-cols)]"
        style={{ ['--flow-cols' as string]: template }}
      >
        {cells.map((cell, index) => {
          const selected = cell.id === selectedId;
          const last = index === cells.length - 1;
          const prev = cells[index - 1];
          // The water cell is a loop, not the next step, so no chevron points into it.
          const notched = Boolean(prev) && cell.kind !== 'utility';
          return (
            <button
              key={cell.id}
              id={`plant-flow-tab-${cell.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="plant-model-stage-panel"
              data-flow-cell={cell.id}
              data-flow-kind={cell.kind}
              onClick={() => onSelect(cell.id)}
              className={cn(
                'flow-cell relative flex min-h-[8.5rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)] lg:border-b-0',
                cell.kind === 'circuit' ? 'pl-4 pr-3.5' : 'pl-3.5 pr-3',
                last ? 'border-r-0' : '',
                notched ? NOTCH : '',
                notched && prev?.id === selectedId ? NOTCH_AFTER_SELECTED : '',
                selected ? SELECTED_FACE : cell.kind === 'utility' ? UTILITY_FACE : IDLE_FACE,
              )}
            >
              {cell.kind === 'circuit' ? <CircuitCellBody cell={cell} /> : <ContextCellBody cell={cell} />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
